import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { usePlatformStore } from "@platform/store";
import {
  joinChimeSession,
  leaveChimeSession,
  stopChimeStreams,
  videoEventBus,
} from "@core/features/video";
import type { MeetingMode } from "@core/features/video";
import {
  joinVideoMeetingApi,
  leaveVideoMeeting as leaveVideoMeetingApi,
} from "@core/features/videoMeeting";
import type { MeetingDetails } from "@core/features/videoMeeting";

type UseJoinVideoMeetingArgs = {
  meetingId: string | null;
  roomId: number | null;
  meetingDetails: MeetingDetails | null;
  region: string;
  enableSimulcast: boolean;
  priorityBasedPolicy?: any;
  keepLastFrameWhenPaused: boolean;
  isEchoReductionEnabled: boolean;
  // Store setters
  setMeetingId: (id: string | null) => void;
  setRoomId: (id: number | null) => void;
  setInitialized: (initialized: boolean) => void;
  setRegion: (region: string) => void;
  setMeetingMode: (mode: MeetingMode) => void;
  setSessionError: (error: string | null) => void;
  setRoomMembersData: (data: any) => void;
  setChimeExternalUserId: (id: string | null) => void;
  resetVideo: () => void;
  resetVideoMeeting: () => void;
};

export function useJoinVideoMeeting({
  meetingId,
  roomId,
  meetingDetails,
  region,
  enableSimulcast,
  priorityBasedPolicy,
  keepLastFrameWhenPaused,
  isEchoReductionEnabled,
  setMeetingId,
  setRoomId,
  setInitialized,
  setRegion,
  setMeetingMode,
  setSessionError,
  setRoomMembersData,
  setChimeExternalUserId,
  resetVideo,
  resetVideoMeeting,
}: UseJoinVideoMeetingArgs) {
  const meetingManager = useMeetingManager();
  const navigateRef = useRef(useNavigate());
  const user = usePlatformStore((s) => s.user);
  const chimeExternalUserId = usePlatformStore((s) => s.chimeExternalUserId);
  const selectedAudioInputDevice = usePlatformStore((s) => s.selectedAudioInputDevice);
  const selectedVideoInputDevice = usePlatformStore((s) => s.selectedVideoInputDevice);
  const selectedAudioOutputDevice = usePlatformStore((s) => s.selectedAudioOutputDevice);

  // Join meeting: call API + Chime session + set devices
  const joinMeeting = useCallback(async () => {
    try {
      setSessionError(null);

      if (!meetingDetails) {
        throw new Error("No meeting details available");
      }

      if (!meetingDetails.video_rooms?.[0]) {
        throw new Error("No video room available for this meeting");
      }

      // Call backend join API
      const joinResponse = await joinVideoMeetingApi(
        {
          meeting_id: meetingDetails.id,
          region,
          echo_reduction: isEchoReductionEnabled,
        },
        user ? { id: user.id } : null
      );

      const chimeMeetingId = joinResponse.chime_meeting_id;

      // Join Chime session
      await joinChimeSession({
        joinInfo: joinResponse.join_info,
        enableSimulcast,
        priorityBasedPolicy,
        keepLastFrameWhenPaused,
        meetingManager,
        setRegion,
        setMeetingMode,
      });

      // Start devices after joining — delay needed for Chime SDK internal
      // initialization to complete before device selection. Removing this
      // causes intermittent device binding failures.
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (selectedAudioInputDevice) {
        try { await meetingManager.startAudioInputDevice(selectedAudioInputDevice); }
        catch (e) { console.warn('[join] Audio input start failed:', e); }
      }
      if (selectedVideoInputDevice) {
        try { await meetingManager.startVideoInputDevice(selectedVideoInputDevice); }
        catch (e) { console.warn('[join] Video input start failed:', e); }
      }
      if (selectedAudioOutputDevice) {
        try { await meetingManager.startAudioOutputDevice(selectedAudioOutputDevice); }
        catch (e) { console.warn('[join] Audio output start failed:', e); }
      }

      // Update store state
      if (chimeMeetingId !== meetingId) {
        setMeetingId(chimeMeetingId);
      }
      if (joinResponse.room_id) {
        setRoomId(joinResponse.room_id);
      }

      // Store the external user ID for multi-tab leave safety
      const externalUserId = joinResponse.join_info?.Attendee?.ExternalUserId || null;
      if (externalUserId) {
        setChimeExternalUserId(externalUserId);
      }

      // Store room members (slice handles array-to-record conversion)
      if (joinResponse.room_members) {
        setRoomMembersData(joinResponse.room_members);
      }

      // Persist session info to sessionStorage (not Zustand persist) so
      // clearExistingSession can clean up after a page refresh. We use
      // sessionStorage rather than the Zustand persist layer because:
      // - sessionStorage auto-clears on tab close (correct lifecycle)
      // - Zustand persist only allows user/authToken; adding video state
      //   would rehydrate stale meeting IDs on every app load
      sessionStorage.setItem(
        "video_session",
        JSON.stringify({
          roomId: joinResponse.room_id,
          meetingId: chimeMeetingId,
          chimeExternalUserId: externalUserId,
        })
      );

      setInitialized(true);

      if (chimeMeetingId && user?.id) {
        videoEventBus.emitEvent("videochat:joined", {
          meetingId: chimeMeetingId,
        });
      }

      // Navigate to meeting page
      navigateRef.current(`/video/meeting/${meetingDetails.id}`);
    } catch (err) {
      console.error("[useJoinVideoMeeting] Join failed:", err);
      setInitialized(false);
      throw err;
    }
  }, [
    meetingId,
    meetingDetails,
    region,
    enableSimulcast,
    priorityBasedPolicy,
    keepLastFrameWhenPaused,
    isEchoReductionEnabled,
    meetingManager,
    selectedAudioInputDevice,
    selectedVideoInputDevice,
    selectedAudioOutputDevice,
    setMeetingId,
    setRoomId,
    setInitialized,
    setRegion,
    setMeetingMode,
    setSessionError,
    setRoomMembersData,
    setChimeExternalUserId,
    user,
  ]);

  // Leave meeting: cleanup streams + API + Chime leave
  const leaveMeeting = useCallback(async () => {
    try {
      setSessionError(null);

      // Send camera-off intent BEFORE teardown so remote participants
      // can use the short roster grace (ROSTER_LEAVE_GRACE_MS).
      // This must be imperative, not via React effects, because the
      // data channel closes during leaveChimeSession().
      try {
        const audioVideo = meetingManager.meetingSession?.audioVideo;
        if (audioVideo) {
          audioVideo.realtimeSendDataMessage(
            "device-state",
            JSON.stringify({
              camera: false,
              mic: false,
              timestamp: Date.now(),
            }),
            30_000
          );
          // Small delay to let the data message reach the SFU before teardown
          await new Promise((r) => setTimeout(r, 100));
        }
      } catch {
        // Best-effort — if this fails, remote will use 30s grace
      }

      await stopChimeStreams(meetingManager);

      if (roomId) {
        try {
          await leaveVideoMeetingApi(roomId, chimeExternalUserId);
        } catch {
          // Non-fatal
        }
      }

      if (meetingId) {
        await leaveChimeSession(meetingManager);
        videoEventBus.emitEvent("videochat:left", { meetingId });
      }

      resetVideo();
      resetVideoMeeting();
      sessionStorage.removeItem("video_session");
    } catch (err) {
      const errorMessage = (err as Error).message;
      setSessionError(errorMessage);
      resetVideo();
      resetVideoMeeting();
      sessionStorage.removeItem("video_session");
      throw err;
    }
  }, [
    meetingId,
    roomId,
    chimeExternalUserId,
    meetingManager,
    setSessionError,
    resetVideo,
    resetVideoMeeting,
  ]);

  // Clear existing session (used when revisiting join page).
  // After a page refresh, Zustand video state resets to null/false,
  // so we fall back to sessionStorage for roomId/meetingId to ensure
  // backend leave API and Chime cleanup still run.
  const clearExistingSession = useCallback(async () => {
    const stored = sessionStorage.getItem("video_session");
    let fallback = null;
    try { fallback = stored ? JSON.parse(stored) : null; } catch {}
    const effectiveRoomId = roomId ?? fallback?.roomId;
    const effectiveMeetingId = meetingId ?? fallback?.meetingId;
    const effectiveExternalUserId = chimeExternalUserId ?? fallback?.chimeExternalUserId;

    if (!effectiveRoomId && !effectiveMeetingId) return;

    try {
      if (effectiveRoomId) {
        try {
          await leaveVideoMeetingApi(effectiveRoomId, effectiveExternalUserId);
        } catch {
          // Non-fatal
        }
      }
      if (effectiveMeetingId) {
        await leaveChimeSession(meetingManager);
      }
    } catch {
      // Silently handle cleanup errors
    }

    sessionStorage.removeItem("video_session");
    setInitialized(false);
  }, [meetingId, roomId, chimeExternalUserId, meetingManager, setInitialized]);

  return {
    meetingManager,
    joinMeeting,
    leaveMeeting,
    clearExistingSession,
  };
}
